/**
 * The catalog's typeface comparison (design system, stage 1): the whole system in another face until the
 * app reloads. The typeface is decided — DM Sans, the tokens' own (5-oct-2026) — and this stays only to set
 * it beside the other candidates on the tablet, until it is taken out.
 *
 * @rule   It only ever writes inline on <html>: `--bz-font` and `data-bz-font-test`. Going back to DM Sans
 *         removes both, so the tokens of src/ui/tokens.css are what draws again.
 * @why    The other faces are fetched from Google Fonts when chosen, once: a production tablet never
 *         downloads them, and DM Sans comes with the app (public/index.html).
 */

/** The faces, DM Sans first (the tokens' own). */
export const FONT_TESTS = {
    dmsans: {},
    instrument: {
        font: '"Instrument Sans", system-ui, sans-serif',
        href: 'https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400..700&display=swap',
    },
    schibsted: {
        font: '"Schibsted Grotesk", system-ui, sans-serif',
        href: 'https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400..700&display=swap',
    },
    system: { font: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif' },
};

/** The steps of the type scale, smallest first: each has its size, line height and tracking token. */
export const TYPE_STEPS = ['xs', 'sm', 'md', 'lg', 'xl', '3xl', 'code'];

/** Which face the comparison has set, read from the document (it lives until a reload). */
export function currentFontTest() {
    return document.documentElement.dataset.bzFontTest || 'dmsans';
}

/** Sets the whole system in a face; DM Sans leaves the tokens' own. */
export function applyFontTest(choice) {
    const root = document.documentElement;
    root.style.removeProperty('--bz-font');
    const test = FONT_TESTS[choice];
    if (!test || !test.font) {
        delete root.dataset.bzFontTest;
        return;
    }
    root.dataset.bzFontTest = choice;
    if (test.href && !document.querySelector(`link[href="${test.href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = test.href;
        document.head.appendChild(link);
    }
    root.style.setProperty('--bz-font', test.font);
}
